import type { PromptFields } from '../../utils/canvas-api'

export interface CanvasPromptTemplate {
  id: string
  name: string
  category: string
  tags: string[]
  description: string
  fields: Required<PromptFields>
  model?: string
}

const emptyFields: Required<PromptFields> = {
  subject: '',
  action: '',
  environment: '',
  composition: '',
  lighting: '',
  camera: '',
  color: '',
  medium: '',
  style: '',
  mood: '',
  quality: '',
}

function fields(input: Partial<Required<PromptFields>>): Required<PromptFields> {
  return { ...emptyFields, ...input }
}

export const CANVAS_TEMPLATE_CATEGORIES = [
  'Portrait',
  'Cinematic',
  'Product',
  'Anime',
  '3D',
  'Fashion',
] as const

export const CANVAS_PROMPT_TEMPLATES: CanvasPromptTemplate[] = [
  {
    id: 'portrait-editorial-rooftop',
    name: 'Editorial Rooftop Portrait',
    category: 'Portrait',
    tags: ['人像', '胶片', '黄昏'],
    description: '适合杂志风人像、个人形象照和叙事封面。',
    fields: fields({
      subject: '一位卷发黑发的 30 岁摩洛哥女性，深邃眼神，亚麻衬衫与银质耳环',
      action: '在天台上安静地啜饮薄荷茶，身体微微侧向镜头',
      environment: '黄昏时分俯瞰非斯老城，远处屋顶与灯火层层铺开',
      composition: '中景人像，主体居中，三分法留白',
      lighting: '黄金时刻暖色侧逆光，柔和长阴影，轻微体积光',
      camera: '35mm 镜头，浅景深，锐利对焦，轻微胶片颗粒',
      color: '暖橘主调与靛蓝阴影，电影感对比',
      medium: '胶片摄影',
      style: '国家地理杂志摄影，写实编辑风',
      mood: '安静、沉思、怀旧',
      quality: '超清细节，4K，真实肤质',
    }),
  },
  {
    id: 'cinematic-rain-street',
    name: 'Rainy Neon Film Still',
    category: 'Cinematic',
    tags: ['电影感', '雨夜', '霓虹'],
    description: '适合电影剧照、音乐封面和赛博都市氛围图。',
    fields: fields({
      subject: '一名穿黑色风衣的年轻侦探，湿发，手持透明雨伞',
      action: '站在路口回头凝视远处车灯',
      environment: '深夜东京小巷，霓虹招牌、积水路面、雨滴飞溅',
      composition: '低机位中远景，对角线构图，前景雨滴虚化',
      lighting: '蓝紫霓虹光，背后车灯形成强烈轮廓光',
      camera: '电影宽银幕构图，50mm，浅景深，慢门拖影',
      color: '冷蓝与洋红互补，高对比暗部',
      medium: '电影剧照',
      style: '新黑色电影，赛博朋克边缘感',
      mood: '孤独、紧张、悬疑',
      quality: '电影级光影，HDR，锐利对焦',
    }),
  },
  {
    id: 'product-luxury-watch',
    name: 'Luxury Watch Campaign',
    category: 'Product',
    tags: ['产品', '广告', '奢侈品'],
    description: '适合电商主图、产品广告和品牌视觉探索。',
    fields: fields({
      subject: '一枚钛金属机械腕表，蓝宝石表镜，精密镂空机芯',
      action: '静置在黑色玄武岩台面上，表盘微微朝向镜头',
      environment: '极简暗色摄影棚，背景有细微烟雾与柔和反射',
      composition: '产品特写，中心构图，低角度英雄视角',
      lighting: '窄条柔光箱勾勒金属边缘，顶部微弱高光',
      camera: '100mm 微距镜头，焦点锁定表盘细节',
      color: '黑色、钛灰、深蓝高光，低饱和奢华色调',
      medium: '商业产品摄影',
      style: '高端腕表广告，极简奢侈品视觉',
      mood: '冷静、精密、昂贵',
      quality: '超清细节，8K，真实金属材质',
    }),
  },
  {
    id: 'anime-summer-train',
    name: 'Summer Train Anime',
    category: 'Anime',
    tags: ['动漫', '夏日', '青春'],
    description: '适合轻小说插图、动画分镜和青春主题视觉。',
    fields: fields({
      subject: '一名穿白色短袖校服的少女，肩背帆布包，发梢被风吹起',
      action: '站在乡间列车站台上望向远方',
      environment: '盛夏午后的海边小站，蓝天白云，远处有闪光海面',
      composition: '横向全景，人物位于画面右侧，大片天空留白',
      lighting: '明亮夏日阳光，柔和反射光，轻微镜头光晕',
      camera: '动画电影分镜视角，35mm，清晰线条',
      color: '清透蓝白色调，少量暖黄色点缀',
      medium: '动漫插画',
      style: '新海诚风格，细腻背景美术',
      mood: '青春、期待、温柔',
      quality: '高细节背景，干净线稿，4K',
    }),
  },
  {
    id: 'three-d-cozy-mascot',
    name: 'Cozy 3D Mascot',
    category: '3D',
    tags: ['3D', 'IP', '可爱'],
    description: '适合品牌吉祥物、头像和轻量 IP 概念。',
    fields: fields({
      subject: '一只圆润的原创云朵吉祥物，豆豆眼，短小手臂',
      action: '抱着一杯热可可坐在木质小凳上',
      environment: '温暖的迷你咖啡角落，背景有柔软抱枕和小灯串',
      composition: '正面中景，主体占画面 60%，干净背景',
      lighting: '柔和暖光，环境光包裹，轻微边缘高光',
      camera: '正交 3D 视角，轻微俯视，柔和景深',
      color: '奶白、焦糖棕、浅薄荷绿，低饱和治愈配色',
      medium: '3D 渲染',
      style: '皮克斯式圆润角色，玩具质感',
      mood: '温暖、治愈、亲切',
      quality: '次表面散射，柔软材质，高清渲染',
    }),
  },
  {
    id: 'fashion-metallic-editorial',
    name: 'Metallic Fashion Editorial',
    category: 'Fashion',
    tags: ['时尚', '棚拍', '金属'],
    description: '适合时装大片、品牌 moodboard 和视觉提案。',
    fields: fields({
      subject: '一位高级冷艳五官的超模，银色金属感长裙，湿发后梳',
      action: '站在镜面水台中央，单手扶住腰线，目光直视镜头',
      environment: '极简黑色摄影棚，浅水反射地面，远处烟雾弥散',
      composition: '竖版全身人像，中央构图，强轮廓剪影',
      lighting: '硬质顶光与两侧条形边缘光，金属反射高光',
      camera: '85mm 时尚摄影视角，低机位，锐利对焦',
      color: '银色、黑色、冷白高光，低饱和高级感',
      medium: '时尚棚拍摄影',
      style: '高级定制服装广告，Vogue 编辑风',
      mood: '冷艳、未来、强势',
      quality: 'RAW 质感，8K，真实布料与反射',
    }),
  },
]
